/**
 * BulkDeleteConfirm — Bulk-Soft-Delete ("In den Papierkorb verschieben").
 *
 * Sprint UI-D2a: dünner Wrapper um `ConfirmDialog`. Soft-Delete ist
 * recoverbar via /trash → kein `danger`-Variant.
 *
 * Session 30 (Delete-UX): zeigt einen Thumbnail-Snippet der betroffenen Assets
 * (Versehens-Schutz bei großer Selektion) + bietet nach dem Soft-Delete einen
 * „Rückgängig"-Toast (restore der gerade verschobenen Assets).
 *
 * `assetIds` = die VOLLE Selektion (Quelle der Wahrheit für Delete + Restore;
 * enthält ggf. Stapel-`memberIds`, die nicht als Einzel-Cards geladen sind).
 * `previewAssets` = die sichtbare Teilmenge mit Thumbnails (nur fürs Snippet) —
 * kann kleiner als `assetIds` sein; die „+N"-Kachel zählt die Differenz.
 */
import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useToast } from '../lib/toast-context';
import { TRASH_CHANGED, ASSETS_CHANGED } from '../lib/events';
import { ConfirmDialog } from './ui/ConfirmDialog';
import { hasSeenTip, markTipSeen } from '../lib/use-contextual-tip';
import type { AssetListItem } from '../lib/asset-types';

export type DeletablePreview = Pick<
  AssetListItem,
  'id' | 'displayFilename' | 'thumbUrl' | 'assetType'
>;

type Props = {
  /** Volle Selektion (Delete + Restore). */
  assetIds: string[];
  /** Sichtbare Teilmenge mit Thumbnails — nur fürs Snippet. */
  previewAssets: DeletablePreview[];
  onClose: () => void;
  onSuccess: () => void;
};

type BulkResponse = {
  success: number;
  failed: Array<{ assetId: string; error: string }>;
};

const THUMB_CAP = 10;

const TYPE_BADGE: Record<DeletablePreview['assetType'], string> = {
  img: 'IMG',
  video: 'VID',
  pdf: 'PDF',
  logo: 'LOGO',
};

export function BulkDeleteConfirm({ assetIds, previewAssets, onClose, onSuccess }: Props) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  // Undo: restore der gerade verschobenen Assets. Läuft aus dem Toast-Callback
  // (überlebt das Unmount des Dialogs, da der Toast im ToastProvider lebt).
  async function restore(ids: string[]): Promise<void> {
    try {
      const res = await api<BulkResponse>('/api/assets/bulk/restore', {
        method: 'POST',
        body: JSON.stringify({ assetIds: ids }),
      });
      if (res.success > 0) {
        window.dispatchEvent(new Event(ASSETS_CHANGED)); // Library re-fetch
        window.dispatchEvent(new Event(TRASH_CHANGED)); // Trash-Counter
      }
      toast.success(
        `${res.success} ${res.success === 1 ? 'Asset' : 'Assets'} wiederhergestellt.`,
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Wiederherstellen fehlgeschlagen');
    }
  }

  async function handleDelete(): Promise<void> {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api<BulkResponse>('/api/assets/bulk', {
        method: 'DELETE',
        body: JSON.stringify({ assetIds }),
      });
      if (res.success > 0) {
        window.dispatchEvent(new Event(TRASH_CHANGED));
        const failedSet = new Set(res.failed.map((f) => f.assetId));
        const restorableIds = assetIds.filter((id) => !failedSet.has(id));
        const baseMsg =
          res.failed.length === 0
            ? `${res.success} ${res.success === 1 ? 'Asset' : 'Assets'} in den Papierkorb verschoben.`
            : `${res.success} verschoben, ${res.failed.length} fehlgeschlagen.`;
        // Onboarding-Tipp (delete-undo): EINMALIG einen Undo-Hinweis in den Toast.
        // Ein Spotlight auf einem 8s-auto-dismissenden Toast wäre schlechte UX →
        // Text-Tipp statt Coachmark (gleicher gf-tip-localStorage-Flag).
        const firstUndo = !hasSeenTip('delete-undo');
        if (firstUndo) markTipSeen('delete-undo');
        const msg = firstUndo
          ? `${baseMsg} Tipp: „Rückgängig“ stellt alles sofort wieder her.`
          : baseMsg;
        // Doppel-Klick-Guard: der Action-Toast lebt 8s und dismisst erst NACH
        // onClick → ein schneller Doppel-Klick würde sonst 2× restoren. Closure-
        // Flag (überlebt das Dialog-Unmount, da der Toast im Provider lebt).
        let undoFired = false;
        toast.withAction(
          msg,
          {
            label: 'Rückgängig',
            onClick: () => {
              if (undoFired) return;
              undoFired = true;
              void restore(restorableIds);
            },
          },
          res.failed.length === 0 ? 'success' : 'info',
        );
      } else {
        toast.error(`Verschieben fehlgeschlagen (${res.failed.length}).`);
        console.warn('Bulk-trash failures:', res.failed);
      }
      onSuccess();
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Verschieben fehlgeschlagen';
      setError(message);
      setSubmitting(false);
    }
  }

  const shown = previewAssets.slice(0, THUMB_CAP);
  const extra = assetIds.length - shown.length;

  return (
    <ConfirmDialog
      open
      onClose={onClose}
      onConfirm={handleDelete}
      loading={submitting}
      title="In den Papierkorb verschieben?"
      confirmLabel="Ja, in den Papierkorb"
    >
      <p className="text-sm text-brand-green-800">
        {assetIds.length} {assetIds.length === 1 ? 'Asset wird' : 'Assets werden'}{' '}
        aus der Library entfernt und in den Papierkorb verschoben.
      </p>
      {/* Thumbnail-Snippet — zeigt, was gelöscht wird (Versehens-Schutz). */}
      {shown.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5" aria-hidden="true">
          {shown.map((a) => (
            <div
              key={a.id}
              className="h-10 w-10 overflow-hidden rounded bg-ink-100"
              title={a.displayFilename}
            >
              {a.thumbUrl ? (
                <img
                  src={a.thumbUrl}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center font-mono text-2xs font-bold text-brand-green-700">
                  {TYPE_BADGE[a.assetType]}
                </div>
              )}
            </div>
          ))}
          {extra > 0 && (
            <div className="flex h-10 w-10 items-center justify-center rounded bg-brand-green-100 font-mono text-2xs font-semibold text-brand-green-700">
              +{extra}
            </div>
          )}
        </div>
      )}
      <p className="mt-3 text-xs text-brand-green-600">
        Wiederherstellbar über <span className="font-mono">/trash</span> oder „Rückgängig" im
        Hinweis. Tag- und Facetten-Verknüpfungen bleiben erhalten.
      </p>
      {error && (
        <p className="mt-3 text-xs text-red-600" role="alert">
          {error}
        </p>
      )}
    </ConfirmDialog>
  );
}
